import axios, { type AxiosError } from 'axios';

import { EMAIL_NOT_VERIFIED_CODE } from '~/services/http/email-not-verified';
import modalStatusService from '~/services/modalStatus';

interface ValidationDetail {
  field: string;
  fieldLabel: string;
  message: string;
}

interface NestErrorBody {
  statusCode?: number;
  error?: string;
  code?: string;
  message?: string | string[];
  details?: ValidationDetail[];
}

export interface ShowApiErrorOptions {
  title?: string;
  description?: string;
  fallbackDescription?: string;
  buttonLabel?: string;
  onDismiss?: () => void;
}

function apiMessageOf(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    if (!error.response) return null;

    const body = error.response.data as NestErrorBody | string | undefined;

    if (typeof body === 'string' && body.trim()) {
      return body;
    }

    if (body && typeof body === 'object') {
      if (Array.isArray(body.details) && body.details.length > 1) {
        return body.details
          .map((d) => `• ${d.fieldLabel}: ${d.message}`)
          .join('\n');
      }
      const { message } = body;
      if (Array.isArray(message) && message.length > 0) {
        return message.join('\n');
      }
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  return null;
}

function genericMessageFor(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) {
      return 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
    }
    if (error.response.status >= 500) {
      return 'Tivemos um problema no servidor. Tente novamente em alguns instantes.';
    }
  }
  return 'Algo não saiu como esperado. Tente novamente.';
}

export function extractErrorMessage(error: unknown): string {
  return (
    apiMessageOf(error) ??
    (error instanceof Error && error.message
      ? error.message
      : genericMessageFor(error))
  );
}

export function getApiErrorCode(error: unknown): string | null {
  if (!axios.isAxiosError(error)) return null;
  const code = (error.response?.data as NestErrorBody | undefined)?.code;
  return typeof code === 'string' ? code : null;
}

function titleFor(error: unknown): string {
  if (axios.isAxiosError(error)) {
    if (!error.response) return 'Sem conexão';
    const status = error.response.status;
    if (status >= 500) return 'Servidor indisponível';
    if (status === 404) return 'Não encontrado';
    if (status === 409) return 'Conflito';
    if (status === 401) return 'Sessão expirada';
    if (status === 403) return 'Ação não permitida';
    if (status === 400 || status === 422) return 'Dados inválidos';
  }
  return 'Ops!';
}

export function showApiError(
  error: unknown,
  options: ShowApiErrorOptions = {},
): false {
  const isSessionExpired =
    axios.isAxiosError(error) && error.response?.status === 401;
  const description =
    options.description ??
    (isSessionExpired
      ? 'Sua sessão expirou. Entre novamente.'
      : (apiMessageOf(error) ??
        options.fallbackDescription ??
        genericMessageFor(error)));
  const title = options.title ?? titleFor(error);
  const textFirstButton = options.buttonLabel ?? 'Entendi';

  modalStatusService.show({
    type: 'error',
    title,
    description,
    showFirstButton: true,
    textFirstButton,
    onClickFirstButton: () => {
      modalStatusService.hide();
      options.onDismiss?.();
    },
    handleCloseModal: () => {
      modalStatusService.hide();
      options.onDismiss?.();
    },
  });

  return false;
}

export function isApiErrorWithStatus(
  error: unknown,
  status: number,
): error is AxiosError {
  return axios.isAxiosError(error) && error.response?.status === status;
}

let navigateTo: ((path: string) => void) | null = null;

export function registerFeedbackNavigator(handler: (path: string) => void) {
  navigateTo = handler;
}

function goTo(path: string) {
  if (navigateTo) navigateTo(path);
  else window.location.assign(path);
}

const UPGRADE_CODES = new Set([
  'FEATURE_LOCKED',
  'CONTRACTS_PER_DOCUMENT_LIMIT',
  'DISTINCT_DOCUMENTS_LIMIT',
]);

const UPGRADE_TITLE: Record<string, string> = {
  FEATURE_LOCKED: 'Recurso do plano pago',
  CONTRACTS_PER_DOCUMENT_LIMIT: 'Limite do plano atingido',
  DISTINCT_DOCUMENTS_LIMIT: 'Limite do plano atingido',
};

function showUpgradeRequiredModal(
  error: unknown,
  code: string,
  options: ShowApiErrorOptions,
): void {
  modalStatusService.show({
    type: 'alert',
    title: UPGRADE_TITLE[code] ?? options.title ?? 'Limite do plano atingido',
    description:
      options.description ??
      apiMessageOf(error) ??
      options.fallbackDescription ??
      'Seu plano atual não permite esta ação. Faça upgrade para continuar.',
    showFirstButton: true,
    textFirstButton: 'Ver planos',
    showSecondButton: true,
    textSecondButton: 'Agora não',
    onClickFirstButton: () => {
      modalStatusService.hide();
      options.onDismiss?.();
      goTo('/app/subscribe');
    },
    onClickSecondButton: () => {
      modalStatusService.hide();
      options.onDismiss?.();
    },
    handleCloseModal: () => {
      modalStatusService.hide();
      options.onDismiss?.();
    },
  });
}

export function handleApiError(
  error: unknown,
  options: ShowApiErrorOptions = {},
): false {
  const code = getApiErrorCode(error);

  if (code === EMAIL_NOT_VERIFIED_CODE) return false;

  if (code && UPGRADE_CODES.has(code)) {
    showUpgradeRequiredModal(error, code, options);
    return false;
  }

  return showApiError(error, options);
}
